/**
 * DQReport — the "why aren't leads qualifying" breakdown. Bars per group
 * (category / study / lead source / recruiter / criterion); clicking a bar
 * drills into the individual disqualifications behind it, with contact links.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import { AnalyticsRow, Disqualification, LandingAnswer } from '../../types';
import { contactName } from '../../types';
import { DQ_CATEGORIES, relTime } from '../../lib';
import BarRow from './BarRow';

export type DQGroupBy = 'category' | 'study' | 'lead_source' | 'recruiter' | 'criterion';

interface DQReportProps {
  groupBy: DQGroupBy;
  from: string | null;
  to: string | null;
  /** Pin the whole report to one study (the StudyDetail Performance view). */
  studyId?: string | null;
}

const dqCategoryLabel = (value: string) =>
  DQ_CATEGORIES.find((c) => c.value === value)?.label || value.replace(/_/g, ' ');

/**
 * Landing-form Q&A list, label-resolved server-side (display-ready strings).
 * Disqualifying answers (kind==='exclude') red, flagged ('review') yellow.
 * Mirrors the profile AttemptDetailModal treatment.
 */
function LandingAnswerList({ answers }: { answers: LandingAnswer[] }) {
  if (!answers.length) {
    return <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No landing form answers</p>;
  }
  return (
    <div className="mt-2 space-y-1.5">
      {answers.map((a, i) => {
        const cls =
          a.kind === 'exclude'
            ? 'border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-900/10'
            : a.kind === 'review'
              ? 'border-amber-300 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-900/10'
              : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';
        return (
          <div key={`${a.question}-${i}`} className={`rounded-lg border p-2 ${cls}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{a.question}</span>
              {a.kind === 'exclude' && (
                <span className="flex-shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  Does not meet
                </span>
              )}
              {a.kind === 'review' && (
                <span className="flex-shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                  Flag
                </span>
              )}
            </div>
            <span className="text-xs font-medium text-gray-900 dark:text-gray-100">{a.answer}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Map a clicked group row onto the drill-down list's filter params. */
function drillParams(groupBy: DQGroupBy, key: string): Record<string, string> | null {
  if (key.startsWith('(')) return null; // '(none)' buckets have no filterable id
  switch (groupBy) {
    case 'category': return { category: key };
    case 'study': return { study_id: key };
    case 'lead_source': return { lead_source: key };
    case 'recruiter': return { recruiter: key };
    case 'criterion': return { criterion_id: key };
    default: return null;
  }
}

export default function DQReport({ groupBy, from, to, studyId = null }: DQReportProps) {
  const [rows, setRows] = useState<AnalyticsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ key: string; label: string } | null>(null);
  const [drillRows, setDrillRows] = useState<Disqualification[] | null>(null);
  // Lazy per-row landing-answer disclosure (PHI: never bulk-embedded in the list
  // payload — fetched on expand only). undefined = not loaded, null = loading,
  // [] = loaded-empty, LandingAnswer[] = loaded.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [answersByDq, setAnswersByDq] = useState<Record<string, LandingAnswer[] | null | undefined>>({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setDrill(null);
    setDrillRows(null);
    api.get('/api/crm/analytics/disqualifications', {
      params: {
        group_by: groupBy,
        from: from || undefined,
        to: to || undefined,
        study_id: studyId || undefined,
      },
    })
      .then((res) => { if (alive) { setRows(res.data?.rows || []); setError(null); } })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || 'Failed to load disqualifications'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [groupBy, from, to, studyId]);

  // Drill-down fetch for the selected bar.
  useEffect(() => {
    if (!drill) return;
    let alive = true;
    setDrillRows(null);
    // A new bar's rows start with no expanded answers (avoid leaking a prior
    // row's PHI into a freshly-loaded list that happens to reuse a key).
    setExpanded(null);
    setAnswersByDq({});
    const params = drillParams(groupBy, drill.key);
    api.get('/api/crm/analytics/disqualifications/list', {
      params: {
        ...(params || {}),
        from: from || undefined,
        to: to || undefined,
        study_id: studyId || (params?.study_id ?? undefined),
        limit: 50,
      },
    })
      .then((res) => { if (alive) setDrillRows(res.data?.disqualifications || []); })
      .catch(() => { if (alive) setDrillRows([]); });
    return () => { alive = false; };
  }, [drill, groupBy, from, to, studyId]);

  const max = useMemo(() => Math.max(...rows.map((r) => Number(r.total)), 1), [rows]);
  const total = useMemo(() => rows.reduce((sum, r) => sum + Number(r.total), 0), [rows]);

  // Toggle + lazily load a single DQ row's study-matched landing answers. Reuses
  // the already-gated GET /contacts/:id (its `screener` is the same buildScreener
  // view as the profile panel); no new /analytics route. PHI is fetched only on
  // expand — the list payload itself never carries answers.
  const toggleAnswers = (d: Disqualification) => {
    if (expanded === d.id) { setExpanded(null); return; }
    setExpanded(d.id);
    if (answersByDq[d.id] !== undefined || !d.contact?.id) return;
    setAnswersByDq((m) => ({ ...m, [d.id]: null })); // loading
    api.get(`/api/crm/contacts/${d.contact.id}`)
      .then((res) => {
        const subs: Array<{ study_title?: string | null; answers?: Array<{ label: string; display: string; eligibility_kind?: string | null }> }> =
          res.data?.screener?.submissions || [];
        // Study-match by title (the only study identifier on the drill row); fall
        // back to the only submission when the contact has exactly one.
        const match = subs.find((s) => s.study_title && d.study_title && s.study_title === d.study_title)
          || (subs.length === 1 ? subs[0] : null);
        const answers: LandingAnswer[] = (match?.answers || []).map((a) => ({
          question: a.label,
          answer: a.display,
          kind: (a.eligibility_kind as LandingAnswer['kind']) || null,
        }));
        setAnswersByDq((m) => ({ ...m, [d.id]: answers }));
      })
      .catch(() => { setAnswersByDq((m) => ({ ...m, [d.id]: [] })); });
  };

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (rows.length === 0) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No disqualifications recorded in this range yet.</p>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Disqualification reasons</h3>
          <span className="text-xs text-gray-400">{total.toLocaleString()} total</span>
        </div>
        <div className="space-y-0.5">
          {rows.map((r) => (
            <BarRow
              key={r.group_key}
              label={groupBy === 'category' ? dqCategoryLabel(r.group_key) : r.group_label}
              count={Number(r.total)}
              max={max}
              hint={total > 0 ? `${Math.round((Number(r.total) / total) * 100)}%` : null}
              color="bg-red-400"
              active={drill?.key === r.group_key}
              onClick={drillParams(groupBy, r.group_key)
                ? () => setDrill((d) => (d?.key === r.group_key ? null : { key: r.group_key, label: r.group_label }))
                : undefined}
            />
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        {!drill ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Click a bar to see the people behind it.</p>
        ) : (
          <>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {groupBy === 'category' ? dqCategoryLabel(drill.key) : drill.label}
            </h3>
            {drillRows === null ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : drillRows.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No rows.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {drillRows.map((d) => (
                  <li key={d.id} className="py-2">
                    <div className="flex items-center justify-between gap-2">
                      {d.contact ? (
                        <Link
                          to={`/crm/contacts/${d.contact.id}`}
                          className="min-w-0 truncate text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
                        >
                          {contactName(d.contact)}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-500">(contact)</span>
                      )}
                      <span className="flex-shrink-0 text-xs text-gray-400">{relTime(d.occurred_at)} ago</span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {d.study_title || 'Study'} · {dqCategoryLabel(d.category)}
                      {d.criterion?.display_question && <> · {d.criterion.display_question.slice(0, 70)}</>}
                    </p>
                    {d.note && <p className="mt-0.5 text-xs italic text-gray-500 dark:text-gray-400">"{d.note}"</p>}
                    {d.contact?.id && (
                      <button
                        type="button"
                        onClick={() => toggleAnswers(d)}
                        className="mt-1 text-xs font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400"
                      >
                        {expanded === d.id ? 'Hide answers' : 'Show answers'}
                      </button>
                    )}
                    {expanded === d.id && (
                      answersByDq[d.id] === null || answersByDq[d.id] === undefined ? (
                        <p className="mt-1 text-xs text-gray-400">Loading…</p>
                      ) : (
                        <LandingAnswerList answers={answersByDq[d.id] as LandingAnswer[]} />
                      )
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
